'use client';
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { Button, Card, Overlay } from '@ui';
import { Input } from '@ui';
import { IconCloseX, IconSearch } from '@icons';
import { prefersReducedMotion } from '@utils/prefersReducedMotion';

export type CPCommand = { label: string; meta?: string; run?: () => void };

export type CPHandle = {
  openPalette: () => void;
  closePalette: () => void;
  togglePalette: () => void;
  isOpen: () => boolean;
};

interface CommandPaletteProps {
  /** Commands shown in the palette (filter + run). Pass section links as `{ label, meta: '#section-id' }` for in-page scroll. */
  commands?: CPCommand[];
  /** Render a trigger button? default true */
  showTrigger?: boolean;
  /** Label for the trigger button */
  triggerLabel?: React.ReactNode;
  /** Optional id for overlay */
  id?: string;
  /** Optional className applied to the Card (panel) */
  className?: string;
  /** CSS class applied to the trigger Button (if shown) */
  triggerClassName?: string;
  /** Alias for triggerLabel; friendly prop name for the trigger's label */
  label?: React.ReactNode;
  /** Controlled open state. If provided, component becomes controlled. */
  open?: boolean;
  /** Callback when open state changes (controlled or uncontrolled). */
  onOpenChange?: (open: boolean) => void;
}

const defaultCommands: CPCommand[] = [];

function CommandPaletteInner(props: CommandPaletteProps, ref: React.Ref<CPHandle>) {
  const {
    commands = defaultCommands,
    showTrigger = true,
    triggerLabel = 'Open Command Palette',
    id = 'cmdkModal',
    className,
    open: openProp,
    onOpenChange,
    triggerClassName,
    label,
  } = props as CommandPaletteProps;
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [filtered, setFiltered] = useState<CPCommand[]>(commands);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const isControlled = typeof openProp === 'boolean';
  const isOpen = isControlled ? openProp! : internalOpen;

  const setOpenState = useCallback(
    (v: boolean) => {
      if (isControlled) {
        onOpenChange?.(v);
      } else {
        setInternalOpen(v);
      }
    },
    [isControlled, onOpenChange]
  );

  // Imperative handle for programmatic control
  useImperativeHandle(
    ref,
    () => ({
      openPalette: () => setOpenState(true),
      closePalette: () => setOpenState(false),
      togglePalette: () => setOpenState(!isOpen),
      isOpen: () => isOpen,
    }),
    [setOpenState, isOpen]
  );

  useEffect(() => {
    const s = query.trim().toLowerCase();
    setFiltered(
      s
        ? commands.filter(
            (c) =>
              c.label.toLowerCase().includes(s) || (!!c.meta && c.meta.toLowerCase().includes(s))
          )
        : commands
    );
    setActive(0);
  }, [query, commands]);

  // Keyboard shortcut to open
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpenState(true);
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        }, 10);
        setQuery('');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [setOpenState]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 10);
    }
  }, [isOpen]);

  const handleModalKeyDown = (e: React.KeyboardEvent) => {
    if (filtered.length === 0) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpenState(false);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runActive();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpenState(false);
    }
  };

  const runActive = () => {
    const cmd = filtered[active];
    if (!cmd) return;
    if ('run' in cmd && typeof cmd.run === 'function') {
      cmd.run();
      setOpenState(false);
      return;
    }

    if (cmd.meta) {
      const raw = cmd.meta || '';
      const idTarget = raw.startsWith('#') ? raw.slice(1) : raw;
      setOpenState(false);

      const doNavigation = () => {
        try {
          if (history && typeof history.pushState === 'function') {
            history.pushState(null, '', `#${idTarget}`);
          } else {
            window.location.hash = `#${idTarget}`;
          }
        } catch {
          // ignore
        }

        const el = document.getElementById(idTarget);
        if (!el) return;

        let headerHeight = 0;
        try {
          const docStyle = getComputedStyle(document.documentElement);
          const v = docStyle.getPropertyValue('--header-height') || '0';
          headerHeight = parseInt(v.replace('px', '').trim(), 10) || 0;
        } catch {
          headerHeight = 0;
        }

        const targetTop = el.getBoundingClientRect().top + window.scrollY - headerHeight;

        let attempts = 0;
        const maxAttempts = 8;

        const tryScroll = () => {
          attempts += 1;
          const behavior: ScrollBehavior = prefersReducedMotion()
            ? 'auto'
            : attempts < maxAttempts
              ? 'smooth'
              : 'auto';
          window.scrollTo({
            top: Math.max(0, Math.round(targetTop)),
            behavior,
          });

          const delta = Math.abs(window.scrollY - Math.round(targetTop));
          if (delta > 20 && attempts < maxAttempts) {
            requestAnimationFrame(tryScroll);
          }
        };

        setTimeout(() => requestAnimationFrame(tryScroll), 80);
      };

      setTimeout(doNavigation, 180);
      return;
    }

    setOpenState(false);
  };

  const handleClose = useCallback(() => setOpenState(false), [setOpenState]);

  return (
    <>
      {showTrigger && (
        <Button
          id={'cmdkOpenBtn'}
          className={triggerClassName ?? 'btn-secondary'}
          onClick={() => {
            setOpenState(true);
            setTimeout(() => {
              inputRef.current?.focus();
              inputRef.current?.select();
            }, 10);
            setQuery('');
          }}
          label={label ?? triggerLabel}
        />
      )}

      {isOpen && (
        <Overlay
          open={isOpen}
          id={id}
          role='dialog'
          aria-modal='true'
          aria-labelledby='cmdkTitle'
          onClose={handleClose}
        >
          <Card
            className={`modal-window size-md cmdk-panel ${className ?? ''}`}
            role='document'
            onKeyDown={handleModalKeyDown}
          >
            <header className='modal-header'>
              <h3 className='modal-title' id='cmdkTitle'>
                Jump to sections
              </h3>
              <Button
                className='btn-close'
                aria-label='Close'
                data-close-modal
                onClick={handleClose}
                icon={<IconCloseX />}
              />
            </header>
            <div className='modal-body'>
              <div className='cmdk-search'>
                <span className='cmdk-icon' aria-hidden='true'>
                  <IconSearch className='ts-ic-sm' />
                </span>
                <Input
                  id='cmdkInput'
                  placeholder='Search commands… (e.g., tabs, forms, demo)'
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  ref={inputRef}
                  type='text'
                />
                <kbd className='cmdk-kbd'>⌘K</kbd>
              </div>
              <div
                className='cmdk-list'
                id='cmdkList'
                role='listbox'
                aria-label='Commands'
                ref={listRef}
              >
                {filtered.map((cmd, i) => (
                  <div
                    className={`cmdk-item${i === active ? ' active' : ''}`}
                    role='option'
                    aria-selected={i === active}
                    data-idx={i}
                    key={`${cmd.meta ?? 'run'}:${cmd.label}`}
                    onClick={() => {
                      setActive(i);
                      runActive();
                    }}
                    onMouseMove={() => setActive(i)}
                  >
                    <span>{cmd.label}</span>
                    {cmd.meta && !/^#/.test(cmd.meta) ? (
                      <span className='cmdk-meta'>{cmd.meta}</span>
                    ) : null}
                  </div>
                ))}
              </div>
              <small className='hint'>↑/↓ to move • Enter to run • ESC to close</small>
            </div>
          </Card>
        </Overlay>
      )}
    </>
  );
}

const CommandPalette = forwardRef<CPHandle, CommandPaletteProps>(CommandPaletteInner);
CommandPalette.displayName = 'CommandPalette';

export default CommandPalette;
